/**
 * Werkshop module catalog — canonical list of first-party
 * plugins the Store renders + gates on. Keyed by registry id
 * so `useModuleEntitlement('deep-sub')` can walk the list
 * without duplicating shape declarations.
 *
 * Wave 7 scaffolding: the prices and license tiers are modelled
 * here but the Stripe price IDs are environment-driven and
 * deliberately absent — the checkout flow that converts a
 * `{ moduleId, license, cadence }` cart item into a real Stripe
 * price ID lands in a follow-up PR that provisions the SKUs
 * in test mode. Until then the catalog drives the UI with
 * "Available soon" CTAs.
 *
 * Two modules ship `free: true` and never gate on
 * `addOnEntitlements` — the Sequencer and MIDI Router. Everything
 * else requires a Personal or Commercial license.
 */
/** The full first-party catalog. Append-only — removing an id
 *  would orphan any existing entitlements persisted on a user's
 *  SUB record. */
export const PLUGIN_CATALOG = [
    // ── Core (always free) ──────────────────────────────────
    {
        id: 'sequencer-core',
        label: 'Sequencer',
        tagline: 'Step grid · pads · samples · MIDI out',
        description: 'The Werkshop sequencer. Step grid with per-pad banks, Scrambler, project save, bus routing.',
        category: 'core',
        free: true,
        badge: 'free',
    },
    {
        id: 'midi-router',
        label: 'MIDI Router',
        tagline: 'Patchbay routing for hardware MIDI',
        description: 'Route hardware MIDI inputs to outputs, the virtual keyboard, or the sequencer.',
        category: 'core',
        free: true,
        badge: 'free',
    },
    {
        id: 'fft-analyzer',
        label: 'FFT Analyzer',
        tagline: 'Real-time spectrum analyzer · SPAN-style',
        description: 'Drop the FFT Analyzer on any bus for a real-time spectrum view. Configurable FFT size, smoothing, pink-noise slope tilt, stereo L/R overlay, and peak-hold trace — matches the SPAN reference look musicians already trust.',
        category: 'effect-utility',
        free: true,
        badge: 'free',
    },
    {
        id: 'lo-fi',
        label: 'LO-FI',
        tagline: 'Bit-crush + classic-sampler presets',
        description: "Live bit-crush on any bus with one-tap presets — S950 (12-bit), SP-1200 (8-bit), Amiga (8-bit), or Custom. Parallel to the Slicer's offline LO-FI bake tool; both share the same preset shapes.",
        category: 'effect-distortion',
        free: true,
        badge: 'free',
    },
    {
        id: 'tone-lfo-tremolo',
        label: 'LFO Tremolo',
        tagline: 'Free-rate amplitude modulation · sine/tri/sq/saw',
        description: 'Classic tremolo with a full LFO surface — rate, depth, shape, and phase. Mirrors the Deep Sub internal LFO so users form one mental model for built-in and bus-chain modulation.',
        category: 'effect-modulation',
        free: true,
        badge: 'free',
    },
    // ── Effects: Web Audio Modules (third-party, MIT/Apache) ─
    //
    // Bundled through `audio/wam/bundle.ts` + loaded via the shared
    // WAM registry. Each entry fills a gap in our first-party catalog.
    {
        id: 'wam-pingo',
        label: 'WAM Pingo',
        tagline: 'Stereo ping-pong delay · WAM',
        description: 'Stereo ping-pong delay with tempo sync. Our first-party delay is mono; Pingo fills the stereo-delay gap via the open Web Audio Modules standard.',
        category: 'effect-time',
        free: true,
        badge: 'free',
        license: 'MIT',
        sourceUrl: 'https://github.com/webaudiomodules/pingo',
    },
    // ── Instruments ─────────────────────────────────────────
    {
        id: 'sampler',
        label: 'Sampler',
        tagline: 'Chop loops into pad-fireable slices',
        description: 'Multi-sample playback with slice markers, pitch control, gain staging, and time conform.',
        category: 'instrument-sampler',
        prices: {
            personal: { monthly: 5, annual: 48, once: 79 },
            commercial: { monthly: 15, annual: 144, once: 249 },
        },
    },
    {
        id: 'deep-sub',
        label: 'Deep Sub',
        tagline: 'Click + fundamental + drive',
        description: 'Sub-bass specialist. Fundamental sine + percussive click + saturated third, summed through a voice envelope.',
        category: 'instrument-synth',
        prices: {
            personal: { once: 29 },
            commercial: { once: 99 },
        },
    },
    {
        id: 'pluck-pad',
        label: 'Pluck Pad',
        tagline: 'Karplus-Strong + delay + reverb',
        description: 'Plucked-string synthesis chained into a ping-pong delay and reverb send.',
        category: 'instrument-synth',
        prices: {
            personal: { once: 29 },
            commercial: { once: 99 },
        },
    },
    {
        id: 'drone',
        label: 'Drone',
        tagline: 'Detuned fat oscillator + filter LFO',
        description: '4-voice FatOscillator chained into a filter with LFO automation for slow sustained beds.',
        category: 'instrument-synth',
        prices: {
            personal: { once: 29 },
            commercial: { once: 99 },
        },
    },
    // ── Tools ───────────────────────────────────────────────
    {
        id: 'piano-roll',
        label: 'Piano Roll',
        tagline: 'Sub-step note editing',
        description: 'Per-pad notes at tick resolution, pitch offsets, independent note lengths, editable in a dedicated full-screen view.',
        category: 'tool',
        prices: {
            personal: { monthly: 3, annual: 28 },
            commercial: { monthly: 9, annual: 80 },
        },
    },
    {
        id: 'automation',
        label: 'Automation',
        tagline: 'Parameter envelopes over time',
        description: 'Breakpoint lanes targeting pad presets, bus effect params, and project-level values. Linear interpolation at trigger time.',
        category: 'tool',
        prices: {
            personal: { monthly: 3, annual: 28 },
            commercial: { monthly: 9, annual: 80 },
        },
    },
];
/** IDs that are free for every user, today and forever. */
export const FREE_PLUGIN_IDS = new Set(PLUGIN_CATALOG.filter(p => p.free).map(p => p.id));
/** Look up a catalog entry by registry id. O(n) scan — fine for
 *  a ~20-entry catalog. */
export function getPluginCatalogEntry(id) {
    return PLUGIN_CATALOG.find(p => p.id === id);
}
export function listPluginsByCategory() {
    const empty = {
        core: [],
        'instrument-synth': [],
        'instrument-sampler': [],
        'instrument-hybrid': [],
        'effect-time': [],
        'effect-modulation': [],
        'effect-dynamics': [],
        'effect-eq': [],
        'effect-distortion': [],
        'effect-utility': [],
        tool: [],
    };
    for (const p of PLUGIN_CATALOG)
        empty[p.category].push(p);
    return empty;
}
//# sourceMappingURL=plugins.js.map